import { z } from 'zod';
import { uuidSchema } from '../common';
import { REPLAY_OVERRIDES, idempotencyHeaderRefusal, type ReplayOverride } from '../../catalog/tool-replay';
import {
  validateToolInputSchema,
  containsKanbanPlaceholders,
  extractToolParameters,
  type SchemaViolation,
} from '../../tool-schema-validator';

/**
 * What a contract refuses in a custom tool's `parameters`: none when they hold an unresolved placeholder (see below),
 * else the validator's violations. Both reads serialize caller JSON, which `JSON.parse` takes far deeper than
 * `JSON.stringify` walks, and each overflows at a depth of its own, so the one catch holds both: a `RangeError` in a
 * refine escapes `safeParse`, a 500 where a refusal is due. In this file, not the validator's, whose module is in
 * Durable Object classes' compile closures, which are at their budgets (`check-backend-startup-graph.ts`).
 */
export function toolParametersViolations(params: unknown): SchemaViolation[] {
  try {
    if (containsKanbanPlaceholders(params)) return [];
    const result = validateToolInputSchema(params);
    return result.ok ? [] : result.errors;
  } catch (error) {
    if (!(error instanceof RangeError)) throw error;
    return [{ path: '', message: 'parameters are nested too deeply to serialize' }];
  }
}

/** A tool name every provider takes as it is. */
export const CALLABLE_TOOL_NAME = /^[A-Za-z_][A-Za-z0-9_-]{0,63}$/;

const nonBlank = (text: unknown): string | undefined => (typeof text === 'string' && text.trim() !== '' ? text : undefined);

/** What a tool config states under `key`, if it states anything there. */
function configText(config: unknown, key: 'name' | 'description'): string | undefined {
  return config !== null && typeof config === 'object' ? nonBlank((config as Record<string, unknown>)[key]) : undefined;
}

/** The name a tool config states, if it states one: what the model is offered the tool as. */
export function toolConfigName(config: unknown): string | undefined {
  return configText(config, 'name');
}

/** What the web form's default config holds for a description until someone writes one. Stored rows still hold it. */
export const DEFAULT_TOOL_CONFIG_DESCRIPTION = 'Function description';

/** A description's text, if it says anything: the form's placeholder says nothing. */
export function statedToolDescription(text: unknown): string | undefined {
  const description = nonBlank(text);
  return description === DEFAULT_TOOL_CONFIG_DESCRIPTION ? undefined : description;
}

/** The description a tool config states, if it states one. */
export function toolConfigDescription(config: unknown): string | undefined {
  return statedToolDescription(configText(config, 'description'));
}

/** What the model is told a tool does: its config's description, else `tool_description`. */
export function offeredToolDescription(config: unknown, toolDescription: unknown): string | undefined {
  return toolConfigDescription(config) ?? nonBlank(toolDescription);
}

/** A tool config as the model is offered it: stating {@link offeredToolDescription}. */
export function withOfferedToolDescription<T>(config: T, toolDescription: unknown): T {
  const description = offeredToolDescription(config, toolDescription);
  if (description === undefined || typeof config !== 'object' || config === null || Array.isArray(config)) return config;
  return (config as { description?: unknown }).description === description ? config : { ...config, description };
}

/**
 * Configure-time guard for `tool_config.parameters` on REST custom-tool
 * routes (gh #1542). Adds draft-2020-12 violations to the Zod context so the
 * Hono handler emits a 400 with structured messages before we cross the
 * HubDO boundary. The HubDO chokepoint re-validates as defense in depth.
 *
 * IMPORTANT: unresolved `[KANBAN_STATUS_*]` placeholder tokens are SKIPPED
 * here. They would otherwise block legitimate template pushes — the chokepoint
 * renders them first, then validates.
 */
function refineToolConfig(value: Record<string, unknown>, ctx: z.RefinementCtx): void {
  const raw = value.tool_config;
  if (raw == null) return;

  const params = extractToolParameters(raw);
  if (params === undefined) {
    // Either tool_config is not an object shape, or there's no `parameters`
    // field to validate. Only the former is an error.
    const looksLikeObject = raw !== null
      && typeof raw === 'object'
      && !Array.isArray(raw)
      || (typeof raw === 'string' && (() => {
        try { const parsed = JSON.parse(raw); return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed); }
        catch { return false; }
      })());
    if (!looksLikeObject) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tool_config'],
        message: 'tool_config must be an object',
      });
    }
    return;
  }

  for (const err of toolParametersViolations(params)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['tool_config', 'parameters', ...err.path.split('.').filter(Boolean)],
      message: err.suggestion ? `${err.message} — ${err.suggestion}` : err.message,
    });
  }
}

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

export const toolIdParam = z.object({ id: uuidSchema });

export const listToolsQuery = z.object({ agent_id: uuidSchema, hub_id: uuidSchema });

export const toolAgentQuery = z.object({ agent_id: uuidSchema, hub_id: uuidSchema });

export const deleteToolQuery = z.object({ agent_id: uuidSchema.optional(), hub_id: uuidSchema });

export const toolConnectionsQuery = z.object({ hub_id: uuidSchema });

export const nativeToolIdSchema = z.string().regex(/^nt-\d{3,}$/, 'Invalid native tool id');

/**
 * Context-boundary for a `delegate_to_hub` (hub-as-agent) delegation — what of
 * the originating conversation crosses into the target hub's spawned task
 * (consultant-agents H1.5). Hubs can differ in team and data-sensitivity posture,
 * so what crosses must be an explicit admin choice, never a default.
 * - `instruction` — only the model's `request` argument (nothing from the transcript)
 * - `summary`     — the request plus the rolling conversation summary
 * - `transcript`  — the request plus the full customer transcript
 * Enforced by the spawn handler in PR 7; stored on the tool row's
 * `execution_config.context_boundary`.
 */
export const CONTEXT_BOUNDARIES = ['instruction', 'summary', 'transcript'] as const;
export const contextBoundarySchema = z.enum(CONTEXT_BOUNDARIES);
export type ContextBoundary = z.infer<typeof contextBoundarySchema>;
export const DEFAULT_CONTEXT_BOUNDARY: ContextBoundary = 'summary';

export const addNativeToolBody = z.object({
  agent_id: uuidSchema,
  hub_id: uuidSchema,
  tool_native_id: nativeToolIdSchema,
  delegation_type: z.enum(['team', 'agent', 'hub']).optional(),
  delegation_id: z.string().optional(),
  delegation_name: z.string().optional(),
  /** Only meaningful for a `hub` delegation (`delegate_to_hub`); ignored otherwise.
   *  Defaults to `summary` when omitted. Admin-set — the link and boundary require
   *  hub-admin (`hub:write`), never derived from any end user's grants. */
  delegation_context_boundary: contextBoundarySchema.optional(),
  // No `resource_config`: retired with knowledge, its column dropped (HubDO v106). A body that still
  // sends it is accepted, the key stripped.
});

export const addMcpToolBody = z.object({
  agent_id: uuidSchema,
  hub_id: uuidSchema,
  mcp_tool_id: uuidSchema,
});

/**
 * The hub's replay settings on a tool (wayai#5083 Phase 2 PR 6; `withToolReplaySettings` in
 * `catalog/tool-replay.ts` says what each does). Which tool kind takes which is checked where the
 * kind is known, at the HubDO chokepoint (`toolReplaySettingsRefusal`); null clears a setting.
 */
const toolReplaySettingsFields = {
  replay_override: z.enum(REPLAY_OVERRIDES).nullable().optional(),
  idempotency_header: z.string().nullable().optional().superRefine((value, ctx) => {
    const refusal = value == null ? null : idempotencyHeaderRefusal(value);
    if (refusal) ctx.addIssue({ code: z.ZodIssueCode.custom, message: refusal });
  }),
  irreversible: z.boolean().optional(),
};

/** The settings alone, for a writer that validates them outside a REST body (hub.yaml: `ci.ts`). */
export const toolReplaySettingsSchema = z.object(toolReplaySettingsFields);

export const createCustomToolBody = z.object({
  agent_id: uuidSchema,
  hub_id: uuidSchema,
  ...toolReplaySettingsFields,
}).passthrough().superRefine(refineToolConfig);

export const updateCustomToolBody = z.object({
  hub_id: uuidSchema.optional(),
  ...toolReplaySettingsFields,
}).passthrough().superRefine(refineToolConfig);

export const updateToolExecutionConfigBody = z.object({
  agent_id: uuidSchema,
  hub_id: uuidSchema,
  execution_config: z.object({
    keep_in_history: z.boolean().optional(),
  }).nullable(),
});

export function resolveKeepInHistory(
  ec: { keep_in_history?: boolean } | null | undefined,
): boolean {
  return ec?.keep_in_history === true;
}

/** Read a `delegate_to_hub` tool's context-boundary from its `execution_config`.
 *  Returns null for an absent/invalid value so callers can fall back to
 *  `DEFAULT_CONTEXT_BOUNDARY` (and omit it from round-tripped YAML). */
export function resolveContextBoundary(
  ec: { context_boundary?: unknown } | null | undefined,
): ContextBoundary | null {
  const v = ec?.context_boundary;
  return (CONTEXT_BOUNDARIES as readonly string[]).includes(v as string)
    ? (v as ContextBoundary)
    : null;
}

/** Coerce a SQL `keep_in_history` value (0|1|null|boolean) to the "kept" boolean. */
export function isMessageKept(v: number | boolean | null | undefined): boolean {
  return v !== 0 && v !== false;
}

/** Coerce an optional boolean `keep_in_history` to the SQL integer value (default = kept). */
export function keepInHistoryToInt(v: boolean | undefined | null): 0 | 1 {
  return v === false ? 0 : 1;
}

// =============================================================================
// RESPONSE TYPES
// =============================================================================

/** A native tool item as returned by the get_native_tools RPC */
export interface NativeToolResponse {
  tool_id: string | null;
  tool_display_name: string;
  tool_name: string;
  tool_description: string | null;
  tool_config: Record<string, unknown> | null;
  enabled: boolean | null;
  is_native: boolean;
  operation: string | null;
  created_at: string;
  available_action: 'Add' | 'Enable' | 'Disable';
  tool_native_id: string | null;
  connection_display_name: string;
  connector_name: string;
  is_delegation: boolean;
  tool_group: string;
  keep_in_history: boolean;
}

/** An MCP external tool item as returned by the get_native_tools RPC */
export interface McpToolResponse {
  // Installed-only fields (the "Add" catalog branch of getNativeTools omits
  // them); mirrors the optionality of the frontend MCPTool type.
  tool_id?: string | null;
  mcp_tool_id: string;
  tool_display_name: string;
  tool_name: string;
  tool_description: string | null;
  // The LLM function-spec rendered LIVE from the `mcp_tool` catalog (source of
  // truth), not the frozen assignment snapshot — what the agent actually
  // receives (report 1876b8be).
  tool_config?: Record<string, unknown> | null;
  /** When the catalog row was last refreshed by `sync-mcp` (`mcp_tool.updated_at`),
   *  surfaced as a "last synced" indicator. */
  synced_at?: string | null;
  enabled?: boolean | null;
  created_at?: string;
  available_action: 'Add' | 'Enable' | 'Disable';
  connection_display_name: string;
  connection_id: string;
  keep_in_history?: boolean;
}

/** A delegation entity (team or agent); also used for advisor (`consult_agent`) targets */
export interface DelegationEntityResponse {
  id: string;
  name: string;
  type: string;
}

/** A resource available for tool configuration */
export interface ToolResourceResponse {
  resource_id: string;
  resource_name: string;
  resource_description: string | null;
  hub_id: string;
}

/** Full response from the get_native_tools RPC (list tools endpoint) */
export interface ListToolsResponse {
  tools: NativeToolResponse[];
  mcp_tools: McpToolResponse[];
  available_delegation_teams: DelegationEntityResponse[];
  available_delegation_agents: DelegationEntityResponse[];
  /** Hub-as-agent (`delegate_to_hub`) targets — the org's PRODUCTION hubs, minus this
   *  hub's own production (a self-delegation loop). A convenience list for the picker,
   *  NOT an authorization gate: the spawn handler re-resolves the target and re-checks
   *  the same-org constraint on every call. Empty when the org registry is unreadable. */
  available_delegation_hubs: DelegationEntityResponse[];
  available_advisor_agents: DelegationEntityResponse[];
  available_resources: ToolResourceResponse[];
}

/** Tool row as stored in the database */
export interface ToolRowResponse {
  tool_id: string;
  created_at: string;
  tool_method: string | null;
  connection_id: string;
  tool_display_name: string;
  hub_id: string;
  agent_id: string;
  delegated_agent_id: string | null;
  delegated_hub_team_id: string | null;
  delegated_hub_id: string | null;
  enabled: boolean;
  tool_description: string | null;
  tool_group: string;
  tool_headers: Record<string, unknown> | null;
  tool_name: string | null;
  tool_native_id: string | null;
  tool_path: string | null;
  is_native: boolean;
  operation: string | null;
  is_delegation: boolean;
  updated_at: string | null;
  tool_config: Record<string, unknown>;
  tool_instructions: string | null;
  additional_body_params: Record<string, unknown> | null;
  tool_query_params: Record<string, unknown> | null;
  mcp_tool_id: string | null;
  composed_tools: Array<{
    tool_name: string;
    mappings: Record<string, string | { source: string; type?: string; description?: string }>;
    appended_description?: string;
    success_path?: string;
  }> | null;
  execution_config: Record<string, unknown> | null;
  tool_body_format: string;
  expose_schema_to_llm: boolean;
  /** The hub's replay settings on the tool (`toolReplaySettingsFields`). */
  replay_override?: ReplayOverride | null;
  idempotency_header?: string | null;
  irreversible?: boolean;
}

/** Agent row shape used in tool get-by-id join */
export interface ToolAgentResponse {
  agent_id: string;
  agent_name: string;
  created_at: string;
  agent_sid: string | null;
  instructions: string;
  response_format: string;
  updated_at: string | null;
  hub_id: string;
  connection_id: string;
  agent_role: string;
  schema_json: Record<string, unknown> | null;
  schema_name: string | null;
  agent_settings: Record<string, unknown> | null;
  instructions_file_id: string | null;
  enabled: boolean;
  include_message_timestamps: boolean;
}

/** Native tool row shape used in tool get-by-id join */
export interface ToolNativeResponse {
  tool_native_id: string;
  created_at: string;
  tool_display_name: string;
  operation: string;
  tool_method: string;
  tool_description: string;
  tool_name: string;
  connector_id: string;
  tool_group: string;
  is_delegation: boolean;
  is_auto_set: boolean;
  tool_config: Record<string, unknown> | null;
  tool_instructions: string | null;
  execution_config: Record<string, unknown> | null;
}

/** Connection row shape used in tool get-by-id join */
export interface ToolConnectionResponse {
  connection_id: string;
  created_at: string;
  service_name: string | null;
  api_key: string | null;
  updated_at: string | null;
  hub_id: string;
  access_token: string | null;
  access_token_expires_at: string | null;
  access_token_secret_id: string | null;
  account_sid: string | null;
  api_key_secret_id: string | null;
  authentication_type: string;
  enabled: boolean;
  connector_id: string;
  connector_name: string;
  password: string | null;
  password_secret_id: string | null;
  connection_display_name: string | null;
  connector_type: string;
  refresh_token: string | null;
  refresh_token_expires_at: string | null;
  refresh_token_secret_id: string | null;
  state: Record<string, unknown> | null;
  username: string | null;
  base_url: string | null;
  connection_headers: Record<string, unknown> | null;
  settings: Record<string, unknown> | null;
  token_refresh_config: Record<string, unknown> | null;
  next_access_token_refresh_at: string | null;
  next_refresh_token_renewal_at: string | null;
  last_token_refresh_at: string | null;
  last_token_refresh_error: string | null;
  organization_credential_id: string | null;
}

/** Response for GET /tools/:id */
export interface GetToolResponse {
  data: {
    tool: ToolRowResponse & {
      agent: ToolAgentResponse;
      native_tool: ToolNativeResponse | null;
      connection: ToolConnectionResponse;
    };
  };
}

/** Connector subset in tool connections response */
export interface ToolConnectionConnectorResponse {
  connector_type: string;
  connector_name: string;
}

/** Connection item in the tool connections list */
export interface ToolConnectionItemResponse {
  connection_id: string;
  connector_name: string;
  connection_display_name: string | null;
  enabled: boolean;
  connector: ToolConnectionConnectorResponse;
}

/** Response for GET /tools/connections */
export interface ListToolConnectionsResponse {
  data: ToolConnectionItemResponse[];
}

/** Response for POST /tools/native (add native tool) */
export interface AddNativeToolResponse {
  data: Record<string, unknown>;
}

/** Response for POST /tools/mcp (add MCP tool) */
export interface AddMcpToolResponse {
  data: Record<string, unknown>;
}

/** Response for POST /tools/custom (create custom tool) */
export interface CreateCustomToolResponse {
  data: ToolRowResponse[];
}

/** Response for PATCH /tools/:id (update custom tool) */
export interface UpdateCustomToolResponse {
  data: ToolRowResponse[];
}

/** Response for DELETE /tools/:id without agent_id */
export interface DeleteToolResponse {
  ok: true;
}
